import { makeAutoObservable } from "mobx";
import { AlertMessageType } from "@/stores/AlertMessageStore";
import { AxiosInstance, AxiosResponse } from "axios";
import { failure, isFailure, isOk, ok, pending, Result } from "@/common/common";
import {
  AgendaItemType,
  approveAgendaItem,
  attendanceItem,
  ElectronicParticipation,
  GeneralMeetingVoteType,
  GetNoticeForPowerOfAttorneyRecipientResponse,
  GetNoticeForShareholderResponse,
  matterForVoting,
  MatterForVoting,
  meetingChairItem,
  NoticeForShareholderController,
  PoAOtherRecipientModel,
  PoARecipientRole,
  PowerOfAttorneyShareholderOption,
  ReceivedPowerOfAttorney,
  toPoARecipient,
} from "./generalMeetingDomain";
import { LoggedOnUserInfoDTO } from "@/types/UserInfo";
import { DownloadService } from "@/api/DownloadService";
import { logDebug, logError } from "@/utils/log";
import {
  DbotEvent,
  DbotEventDispatcher,
  DecisionItem,
  DetailsPanel,
  ElectronicGeneralMeetingStep,
  Evaluation,
  GeneralMeetingParticipation,
  getDecisionActionLabel,
  getDecisionIcon,
  getNumberOfSharesForShareTypeExcludingOwnShares,
  getSharesForShareholder,
  isInformationMatterInDocument,
  PowerOfAttorneyForGeneralMeeting,
  ProjectContext,
  ProjectJsonData,
  ResendDocumentForViewing,
  shareholdersWithSharesExcludingSelf,
  SharesForShareholder,
  sharesForShareholderByShareType,
  total,
  UpdateElectronicGeneralMeetingStatusEvent,
  updateMinutesLiveParticipationEvent,
  voteLabelUppercase,
} from "@/dbot/common/commonDomain";
import { compact, identity, noop } from "lodash";
import { IconName } from "@/components-v2/iconMapping";
import {
  ShareholderRegisteredAttendance,
  ShareholderVoted,
  SubscriptionContext,
  WebSocketMessage,
} from "@/webSocketDomain";
import Big from "big.js";
import {
  calculateAllVotes,
  GeneralMeetingIndividualVote,
  GeneralMeetingIndividualVotes,
  GeneralMeetingMinutes,
  GeneralMeetingVoting,
  IndividualVote,
  liveVotes,
  NoticeToGeneralMeeting,
} from "@/dbot/common/commonGeneralMeetingDomain";
import { ShareholderDTO } from "@/types/Shareholder";
import { newUuid } from "@/utils/uuid";
import { isBlank } from "@/utils/strings";
import { percentOf } from "@/utils/numbers";
import { getFullName } from "@/utils/Roles";
import {
  createGeneralMeetingVotingModel,
  GeneralMeetingVotesModel,
  GeneralMeetingVotingModel,
  handleRegisterNewSignerResult,
  NewSigner,
  NewSignerForm,
  SelectedSigners,
} from "@/dbot/generalMeetingMinutes/generalMeetingMinutesDomain";
import { RoleType } from "@/types/enums/RoleType";
import { CompanyRole } from "@/types/CompanyInfo";
import { filterObject } from "@/utils/collections";
import { when } from "@/utils/functions";
import { navigateGeneralMeetingPreview, openLinkInNewWindow } from "@/utils/navigation";
import { TMessage } from "@/i18n/tt";
import { DocumentType } from "@/common/documents";
import { EventDispatcher, showSuccess } from "@/events/eventsDomain";
import {
  BooleanFieldModel,
  createEmailField,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { ShareType } from "@/common/share";
import { TagProps } from "@/components-v2/Tag";

export type GeneralMeetingSettings = {
  autoOpenSelfRegistration: boolean;
  autoOpenVoting: boolean;
};

export type GeneralMeetingAnnouncement = {
  message: string;
  copyByEmail: boolean;
};

export enum MeetingSettingsOption {
  AUTO_OPEN_SELF_REGISTRATION = "AUTO_OPEN_SELF_REGISTRATION",
  AUTO_OPEN_VOTING = "AUTO_OPEN_VOTING",
}

type MeetingSettingsDialogState = {
  type: "settings";
  form: MeetingSettingsForm;
  isSubmitting?: boolean;
};
type StartMeetingDialogState = { type: "start"; form: MeetingStartForm; isSubmitting?: boolean };
type EndMeetingDialogState = { type: "end"; form: MeetingEndForm; isSubmitting?: boolean };
type MeetingAnnouncementDialogState = {
  type: "announcement";
  form: MeetingAnnouncementForm;
  isSubmitting?: boolean;
};

export type MeetingDialogState =
  | MeetingSettingsDialogState
  | StartMeetingDialogState
  | EndMeetingDialogState
  | MeetingAnnouncementDialogState;

type ResendNoticeDialogState = {
  type: "resend-notice";
  form: MeetingResendNoticeForm;
};

type RegisterPowerOfAttorneyDialogState = {
  type: "register-power-of-attorney";
  form: RegisterPowerOfAttorneyForm;
};

type CopyNoticeUrlForShareholderDialogState = {
  type: "copy-notice-url-to-clipboard";
  form: CopyNoticeUrlForShareholderForm;
};

export type MeetingAttendanceDialogState =
  | ResendNoticeDialogState
  | RegisterPowerOfAttorneyDialogState
  | CopyNoticeUrlForShareholderDialogState;

const meetingSettingsStartLabel: { [key in MeetingSettingsOption]: TMessage } = {
  [MeetingSettingsOption.AUTO_OPEN_SELF_REGISTRATION]: {
    key: "meetings.settings.open-self-registration-of-attendance",
  },
  [MeetingSettingsOption.AUTO_OPEN_VOTING]: {
    key: "meetings.settings.open-self-registration-of-votes",
  },
};

function findNoticeToGeneralMeeting(jsonData: ProjectJsonData): NoticeToGeneralMeeting | undefined {
  return jsonData.documents.flatMap((d) =>
    d.documentData.type === DocumentType.notice_general_meeting ? [d.documentData] : [],
  )[0];
}

const mapStepToAgendaItem = (
  step: ElectronicGeneralMeetingStep,
  mattersForVoting: MatterForVoting[],
): AgendaItemType | undefined => {
  switch (step.type) {
    case "registration":
      return { type: "attendance" };
    case "choiceOfMeetingChair":
      return { type: "meetingChair" };
    case "approveAgenda":
      return { type: "approveAgenda" };
    case "decision":
      return when(
        mattersForVoting.find((m) => m.decision.decisionId === step.decisionId),
        (matter) => ({ type: "matter", matter }),
      );
  }
};

export type NoticeForShareholderUrl = {
  shareholderId: number;
  url: string;
};

export class AdminElectronicGeneralMeetingController {
  http: AxiosInstance;
  dispatch: DbotEventDispatcher;
  noticeController: NoticePreviewController;
  powerOfAttorney: Map<number, PowerOfAttorneyForGeneralMeeting> = new Map();
  noticeForShareholdersUrl: Map<number, NoticeForShareholderUrl> = new Map();
  shareholderNameFilter: StringFieldModel;
  shareTypeFilter: RadioFieldModel<ShareType | null>;
  shareholderAttendanceFilter: ShareholderAttendanceFilter = "all";
  shareholderVotingFilter: ShareholderVotingFilter = "participating";
  roles: CompanyRole[];
  shareNominalValue: number;
  showRegistrationDialog = false;
  currentVoteType?: GeneralMeetingVoteType;
  decisionId: string | undefined;
  jsonData: ProjectJsonData;
  registeringAttendanceShareholderIds: number[] = [];
  registeringVotesShareholderIds: number[] = [];
  deleteVotesShareholderIds: number[] = [];
  votesModel?: GeneralMeetingVotesModel;
  shareholderById: { [p: string]: ShareholderDTO } = {};
  meetingHandle: () => ElectronicGeneralMeeting | undefined;
  dialogState?: MeetingDialogState;
  attendanceDialogState?: MeetingAttendanceDialogState;
  projectId: string;
  projectContext: ProjectContext;
  result?: Result<void, TMessage>;

  currentAgendaItem?: AgendaItemType;

  newSigner: NewSignerForm | null = null;

  constructor(
    http: AxiosInstance,
    dispatch: DbotEventDispatcher,
    projectContext: ProjectContext,
    jsonData: ProjectJsonData,
    meetingHandle: () => ElectronicGeneralMeeting | undefined,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
    this.http = http;
    this.dispatch = dispatch;
    this.projectId = projectContext.projectId;
    this.projectContext = projectContext;
    this.noticeController = new NoticePreviewController(http, dispatch, noop);
    this.jsonData = jsonData;
    this.roles = projectContext.initialState.roles;
    this.projectContext.initialState.shareholders.forEach((s) => {
      this.shareholderById[`${s.id}`] = s;
    });
    this.shareNominalValue = projectContext.initialState.shareNominalValue;
    this.shareholderNameFilter = new StringFieldModel();
    this.shareTypeFilter = new RadioFieldModel<ShareType | null>({
      value: null,
      options: [null, ...this.shareTypes],
      idFn: (shareType) => (shareType ? String(shareType.sharetypeId) : "total"),
      labelFn: () => ({ translated: "" }),
    });
    this.meetingHandle = meetingHandle;
    const meeting = meetingHandle();
    if (meeting) {
      this.setupForLiveUpdates(projectContext, meeting);
    }
  }

  get orgNo() {
    return this.projectContext.orgNo;
  }

  get isScreenShareMode() {
    return this.projectContext.displayMode === "screen-share";
  }

  get mattersForVoting(): MatterForVoting[] {
    if (isOk(this.noticeControllerResult)) {
      return this.noticeControllerResult.data.response.mattersForVoting;
    }
    return [];
  }

  get shareTypes() {
    return this.projectContext.initialState.shareTypes.filter((st) => !st.historical);
  }

  get sharesForShareholders() {
    return this.projectContext.initialState.sharesForShareholders;
  }

  openNewSigner(
    option: NewSigner,
    signer?: keyof SelectedSigners,
    additionalCosignerIndex?: number,
  ) {
    if (signer) {
      this.newSigner = new NewSignerForm({
        signer: signer,
        inputValue: option.inputValue,
        onSubmit: this.dispatch,
        onCancel: this.closeNewSigner,
      });
    } else if (additionalCosignerIndex !== undefined) {
      this.newSigner = new NewSignerForm({
        additionalCosignerIndex: additionalCosignerIndex,
        inputValue: option.inputValue,
        onSubmit: this.dispatch,
        onCancel: this.closeNewSigner,
      });
    }
  }

  closeNewSigner() {
    this.newSigner = null;
  }

  get newSignerForm() {
    return this.newSigner;
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/open-details-panel":
        switch (event.panel.type) {
          case "generalmeeting":
            this.setCurrentAgendaItem(mapStepToAgendaItem(event.panel.step, this.mattersForVoting));
            break;
        }
        break;
      case "dbot/close-details-panel":
        this.setCurrentAgendaItem(undefined);

        break;
      case "dbot/show-general-meeting-dialog":
        this.setShowGeneralMeetingDialogs(event.voteType, event.decisionId);
        break;
      case "dbot/register-new-signer-result":
        handleRegisterNewSignerResult(event, this.newSigner, this.projectContext, this.dispatch);
        this.newSigner = null;
        break;
      case "dbot/save-project-success":
        if (
          this.projectContext.generalMeetingFields.conductElectronicGeneralMeeting.value === true
        ) {
          this.fetchMeetingPreview(this.projectId);
        }
        break;
      default:
        switch (this.attendanceDialogState?.type) {
          case "resend-notice":
            this.attendanceDialogState.form.handleEvent(event);
            break;
        }
    }
  }

  setCurrentAgendaItem(agendaItem?: AgendaItemType) {
    this.currentAgendaItem = agendaItem;
    this.projectContext.generalMeetingFields.displayConfirmElectronicGMDialog.setValue(false);
  }

  setupForLiveUpdates(projectContext: ProjectContext, meeting: ElectronicGeneralMeeting) {
    if (this.votesModel === undefined || this.subscription === undefined) {
      this.votesModel = createGeneralMeetingVotingModel(
        projectContext,
        findGeneralMeetingMinutes(this.jsonData)!,
        liveVotes(),
      );
      updateVotesModelWithLiveData(meeting, this.projectContext.initialState, this.votesModel);
      this.subscribeToAdminGeneralMeetingMessages(this.projectId);
    }
  }

  get meetingLink() {
    return findNoticeToGeneralMeeting(this.jsonData)?.electronicParticipationLink?.trim();
  }

  setProjectJsonData(jsonData: ProjectJsonData) {
    this.jsonData = jsonData;
  }

  filterAttendanceShareholders(data: AttendanceData) {
    const filteredShareholders = this.getShareholdersByAttendanceFilter(data);
    return this.textSearchFilter(filteredShareholders);
  }

  filterVotingShareholders(data: LiveVotingResults) {
    const filteredShareholders = this.getShareholdersByVotingFilter(data);
    return this.textSearchFilter(filteredShareholders);
  }

  private textSearchFilter(filteredShareholders: ShareholderDTO[]) {
    const inputText = this.shareholderNameFilter.inputValue.toLowerCase();
    return isBlank(inputText)
      ? filteredShareholders
      : filteredShareholders.filter(
          (s) =>
            s.name.toLowerCase().includes(inputText) ||
            getFullName(s.contactInfo).toLowerCase().includes(inputText) ||
            this.powerOfAttorney.get(s.id)?.poaRecipientName.toLowerCase().includes(inputText),
        );
  }

  getSelectedShareCapitalForShareholder(s: ShareholderDTO) {
    return this.getSelectedNumberOfSharesForShareholder(s) * this.shareNominalValue;
  }

  get selectedShareType() {
    return this.shareTypeFilter.value;
  }

  get shareholderAttendanceColumnLabel() {
    switch (this.shareholderAttendanceFilter) {
      case "all":
        return "general-meeting.attendance.recipients";
      case "attending":
        return "general-meeting.attendance.recipientsAttending";
      case "notAttending":
        return "general-meeting.attendance.recipientsNotAttending";
      case "poa":
        return "general-meeting.attendance.recipientsWithPoa";
    }
  }

  get shareholderVotingColumnLabel() {
    switch (this.shareholderVotingFilter) {
      case "participating":
        return "general-meeting.voting.participating";
      case "shareholdersNotVoted":
        return "general-meeting.voting.shareholdersNotVoted";
      case "shareholdersFor":
        return "general-meeting.voting.shareholdersFor";
      case "shareholdersAgainst":
        return "general-meeting.voting.shareholdersAgainst";
    }
  }

  getShareholdersByAttendanceFilter(data: AttendanceData) {
    switch (this.shareholderAttendanceFilter) {
      case "all":
        return this.allShareholders;
      case "attending":
        return data.attendingShareholders;
      case "notAttending":
        return data.shareholdersNotAttending;
      case "poa":
        return this.allShareholders.filter((s) => this.hasPoa(s.id));
    }
  }

  getShareholdersByVotingFilter(data: LiveVotingResults) {
    switch (this.shareholderVotingFilter) {
      case "participating":
        return data.shareholders;
      case "shareholdersNotVoted":
        return data.shareholdersNotVoted;
      case "shareholdersFor":
        return data.shareholdersFor;
      case "shareholdersAgainst":
        return data.shareholdersAgainst;
    }
  }

  *openRegisterPowerOfAttorneyDialog(shareholderId?: number) {
    yield this.loadPowerOfAttorneyData(this.projectId);
    const meeting = this.meetingHandle();

    if (meeting) {
      this.attendanceDialogState = {
        type: "register-power-of-attorney",
        form: new RegisterPowerOfAttorneyForm({
          registerNotAttending: this.registerNotAttending,
          closeDialog: this.closeMeetingAttendanceDialog,
          dispatch: this.dispatch,
          http: this.http,
          projectContext: this.projectContext,
          meeting: meeting,
          powerOfAttorney: this.powerOfAttorney,
          shareholderId: shareholderId,
        }),
      };
    }
  }

  openResendNoticeDialog(shareholderData: ShareholderAttendanceRow) {
    const documentId = this.meetingHandle()?.noticeId;
    if (documentId) {
      this.attendanceDialogState = {
        type: "resend-notice",
        form: new MeetingResendNoticeForm(
          this.closeMeetingAttendanceDialog,
          this.dispatch,
          shareholderData,
          documentId,
        ),
      };
    }
  }

  openCopyNoticeUrlDialog(shareholderData: ShareholderAttendanceRow) {
    const url = this.noticeForShareholdersUrl.get(shareholderData.shareholderId)?.url;
    if (url) {
      this.attendanceDialogState = {
        type: "copy-notice-url-to-clipboard",
        form: new CopyNoticeUrlForShareholderForm(
          this.closeMeetingAttendanceDialog,
          shareholderData,
          url,
          this.projectContext.initialState.company.name,
          this.projectContext.generalMeetingFields.dateGeneralMeeting.validatedValue,
        ),
      };
    }
  }

  closeMeetingAttendanceDialog() {
    this.attendanceDialogState = undefined;
  }

  openMeetingSettingsDialog() {
    this.dialogState = {
      type: "settings",
      form: new MeetingSettingsForm(this.meetingHandle()),
    };
  }

  openStartGeneralMeetingDialog() {
    this.dialogState = {
      type: "start",
      form: new MeetingStartForm(this.meetingHandle()),
    };
  }

  openEndGeneralMeetingDialog() {
    this.dialogState = {
      type: "end",
      form: new MeetingEndForm(this.meetingHandle()),
    };
  }

  openMeetingAnnouncementDialog() {
    this.dialogState = {
      type: "announcement",
      form: new MeetingAnnouncementForm(),
    };
  }

  closeMeetingDialog() {
    this.dialogState = undefined;
  }

  submitMeetingDialog() {
    const state = this.dialogState;
    switch (state?.type) {
      case "start":
        this.startMeeting(state);
        break;
      case "settings":
        this.saveSettings(state);
        break;
      case "end":
        this.endMeeting(state);
        break;
      case "announcement":
        this.meetingAnnouncement(state);
        break;
    }
  }

  get allShareholders() {
    return shareholdersWithSharesExcludingSelf(
      this.projectContext.initialState.shareholders,
      this.sharesForShareholders,
    );
  }

  fetchMeetingPreview(projectId: string) {
    this.noticeController.fetchMeetingPreview(projectId);
  }

  get noticeControllerResult() {
    return this.noticeController.result;
  }

  updateMeeting(meeting: ElectronicGeneralMeeting) {
    if (isOk(this.noticeController.result)) {
      this.noticeController.result.data.response.electronicGeneralMeeting = meeting;
    }
    this.dispatch({ type: "dbot/update-general-meeting", meeting });
  }

  updateMeetingStatus({
    statusName,
    meetingStatus,
    participationList,
    decisionId,
  }: {
    statusName: StatusName;
    meetingStatus: GeneralMeetingStatus | undefined;
    participationList?: GeneralMeetingParticipation[];
    decisionId?: string;
  }) {
    const newStatus =
      meetingStatus === GeneralMeetingStatus.STARTED
        ? GeneralMeetingStatus.FINISHED
        : GeneralMeetingStatus.STARTED;

    this.dispatch(
      compact([
        newStatus === GeneralMeetingStatus.FINISHED &&
          participationList &&
          updateMinutesLiveParticipationEvent(participationList),
        {
          type: "dbot/electronic-general-meeting-update-status",
          projectId: this.projectId,
          statusName: statusName,
          status: newStatus,
          decisionId,
        },
      ]),
    );
  }

  private subscription?: SubscriptionContext;

  setSubscription(subscription: SubscriptionContext) {
    this.subscription = subscription;
  }

  unsubscribeFromGeneralMeetingMessages() {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  subscribeToAdminGeneralMeetingMessages(projectId: string) {
    logDebug("Subscribing to general meeting messages");
    const onMessage = (message: WebSocketMessage) => {
      logDebug("Received message: ", message);
      switch (message.type) {
        case "shareholder-power-of-attorney":
          this.loadPowerOfAttorneyData(message.projectId);
          break;
        case "shareholder-participation":
          this.updateMeetingWithNewParticipation(this.meetingHandle()!, message);
          break;
        case "shareholder-voted":
          this.updateVoting(this.meetingHandle()!, message);
          break;
      }
    };
    this.dispatch({
      type: "subscribe-to-web-socket-messages",
      topic: `/topic/project/${projectId}/admin`,
      onSubscribed: this.setSubscription,
      onMessage,
    });
  }

  private updateVoting(meeting: ElectronicGeneralMeeting, message: ShareholderVoted) {
    if (meeting.projectId === message.projectId) {
      const votes: GeneralMeetingIndividualVotes = meeting.votes ?? { ...emptyIndividualVotes };
      const { shareholderId, vote, voteType, decisionId } = message;
      const attendingShareholders: { [p: string]: ShareholderDTO } = filterObject(
        this.shareholderById,
        (s) => meeting.participationList.some((p) => `${p.shareholderId}` === s && p.isPresent),
      );
      const representedVotes = Object.values(attendingShareholders)
        .map((s) => toNumberOfVotes(s, this.sharesForShareholders, this.shareTypes).reduce(sum, 0))
        .reduce(sum, 0);

      const representedShareCapital = Object.values(attendingShareholders)
        .map((s) =>
          toShareCapital(
            s,
            this.sharesForShareholders,
            this.shareTypes,
            this.shareNominalValue,
          ).reduce(sum, 0),
        )
        .reduce(sum, 0);
      let individualVotes: GeneralMeetingIndividualVote[] = [];
      let votingModel: GeneralMeetingVotingModel | undefined;
      switch (message.voteType) {
        case "meetChair":
          individualVotes = votes.meetChair;
          votingModel = this.votesModel?.meetChair;
          break;
        case "approveNoticeAndAgenda":
          individualVotes = votes.approveNoticeAndAgenda;
          votingModel = this.votesModel?.approveNoticeAndAgenda;
          break;
        case "decision":
          if (votes.decisions[message.decisionId!] === undefined) {
            votes.decisions[message.decisionId!] = [];
          }
          individualVotes = votes.decisions[message.decisionId!];
          votingModel = this.votesModel?.decisions.get(message.decisionId!);
          break;
      }
      const index = individualVotes.findIndex((v) => v.shareholderId === shareholderId);
      if (vote) {
        if (index !== -1) {
          individualVotes[index].vote = vote;
        } else {
          individualVotes.push({ shareholderId, vote, id: message.voteId });
        }
      } else {
        individualVotes.splice(index, 1);
      }
      const voting: GeneralMeetingVoting = calculateAllVotes(
        individualVotes,
        attendingShareholders,
        this.sharesForShareholders,
        this.shareTypes,
        this.shareNominalValue,
        "live",
        representedVotes,
        representedShareCapital,
      );
      votingModel?.update(voting);
      this.dispatch({
        type: "dbot/update-voting-in-general-meeting",
        voting,
        voteType,
        decisionId,
      });
    }
  }

  private updateMeetingWithNewParticipation(
    meeting: ElectronicGeneralMeeting,
    message: ShareholderRegisteredAttendance,
  ) {
    if (meeting.projectId === message.projectId) {
      meeting.participationList = meeting.participationList.filter(
        (p) => p.shareholderId !== message.shareholderId,
      );
      if (message.participation?.participation) {
        meeting.participationList.push(message.participation.participation);
      }
    }
  }

  *updateStatus(event: UpdateElectronicGeneralMeetingStatusEvent) {
    try {
      const result: ElectronicGeneralMeeting = yield this.http
        .post(`/general-meeting/${event.projectId}/update-status`, {
          statusName: event.statusName,
          status: event.status,
          decisionId: event.decisionId,
        })
        .then((r) => r.data);
      this.dispatch({
        type: "dbot/electronic-general-meeting-update-status-result",
        result: ok(result),
      });
    } catch (err) {
      this.dispatch({
        type: "dbot/electronic-general-meeting-update-status-result",
        result: failure(),
      });
    }
  }

  *registerAttending(shareholderId: number, meeting: ElectronicGeneralMeeting) {
    this.registeringAttendanceShareholderIds.push(shareholderId);
    const shareholderParticipation = meeting.participationList.find(
      (p) => p.shareholderId === shareholderId,
    );
    const shareholderPoa = this.powerOfAttorney.get(shareholderId);
    if (shareholderParticipation) {
      try {
        const response: AxiosResponse = yield this.http.post(
          `/project/${this.projectId}/generalMeetingParticipation`,
          {
            createParticipation: [],
            updateParticipation: [
              {
                ...shareholderParticipation,
                isPresent: true,
                representedById: shareholderPoa?.poaRecipientCompanyRoleId,
                representedByName: shareholderPoa?.poaRecipientName,
              },
            ],
          },
        );
        shareholderParticipation.isPresent = true;
        shareholderParticipation.registeredBy = response.data.registeredBy;
        shareholderParticipation.registeredAt = response.data.registeredAt;
      } catch (e) {
        this.result = failure({ key: "general-meeting.attendance.errorRegister" });
      }
    } else {
      const shareholderParticipation = {
        participationId: newUuid(),
        shareholderId: shareholderId,
        isPresent: true,
        representedById: shareholderPoa?.poaRecipientCompanyRoleId,
        representedByName: shareholderPoa?.poaRecipientName,
      };
      try {
        const response: AxiosResponse = yield this.http.post(
          `/project/${this.projectId}/generalMeetingParticipation`,
          {
            createParticipation: [shareholderParticipation],
            updateParticipation: [],
          },
        );
        if (meeting) {
          meeting.participationList.push({
            ...shareholderParticipation,
            registeredBy: response.data.registeredBy,
            registeredAt: response.data.registeredAt,
          });
        }
      } catch (e) {
        this.result = failure({ key: "general-meeting.attendance.errorRegister" });
      }
    }
    this.registeringAttendanceShareholderIds = this.registeringAttendanceShareholderIds.filter(
      (n) => n !== shareholderId,
    );
  }

  *registerNotAttending(shareholderId: number, meeting: ElectronicGeneralMeeting) {
    const shareholderParticipation = meeting.participationList.find(
      (p) => p.shareholderId === shareholderId,
    );
    if (!shareholderParticipation) {
      return;
    }
    try {
      const response: AxiosResponse = yield this.http.post(
        `/project/${this.projectId}/generalMeetingParticipation`,
        {
          createParticipation: [],
          updateParticipation: [
            {
              ...shareholderParticipation,
              isPresent: false,
            },
          ],
        },
      );
      shareholderParticipation.isPresent = false;
      shareholderParticipation.registeredBy = response.data.registeredBy;
      shareholderParticipation.registeredAt = response.data.registeredAt;
    } catch (e) {
      this.result = failure({ key: "general-meeting.attendance.errorRegister" });
    }
  }

  isRegisteringAttendanceForShareholder(shareholderId: number) {
    return this.registeringAttendanceShareholderIds.includes(shareholderId);
  }

  *loadPowerOfAttorneyData(projectId: string) {
    try {
      const poaResponse: AxiosResponse = yield this.http.get(
        `/project/${projectId}/powerOfAttorneys`,
      );
      const powerOfAttorneys: PowerOfAttorneyForGeneralMeeting[] =
        poaResponse.data.powerOfAttorneys;
      this.powerOfAttorney.clear();
      powerOfAttorneys.forEach((poa) => this.powerOfAttorney.set(poa.shareholderId, poa));
    } catch (e) {
      this.result = failure({ key: "general-meeting.attendance.errorInit" });
    }
  }

  *loadNoticeForShareholdersUrl(projectId: string) {
    try {
      const response: AxiosResponse = yield this.http.get(
        `/project/${projectId}/noticeForShareholdersUrl`,
      );
      const urls: NoticeForShareholderUrl[] = response.data;
      this.noticeForShareholdersUrl.clear();
      urls.forEach((url) => this.noticeForShareholdersUrl.set(url.shareholderId, url));
    } catch (e) {
      this.result = failure({ key: "general-meeting.copy-participation-link.errorInit" });
    }
  }

  getPoaName(s: ShareholderDTO): string | undefined {
    return this.powerOfAttorney.get(s.id)?.poaRecipientName;
  }

  hasPoa(shareholderId: number) {
    return this.powerOfAttorney.has(shareholderId);
  }

  setSelectedAttendanceFilter(filterValue: ShareholderAttendanceFilter) {
    this.shareholderAttendanceFilter = filterValue;
  }

  setSelectedVotingFilter(filterValue: ShareholderVotingFilter) {
    this.shareholderVotingFilter = filterValue;
  }

  getPercentageOfSharesForShareholder(s: ShareholderDTO) {
    const selectedShareType = this.selectedShareType;
    const numberOfSharesForShareholder = this.getSelectedNumberOfSharesForShareholder(s);
    return selectedShareType
      ? percentOf(
          numberOfSharesForShareholder,
          getNumberOfSharesForShareTypeExcludingOwnShares(
            selectedShareType,
            this.projectContext.initialState,
          ),
        )
      : percentOf(numberOfSharesForShareholder, this.totalSharesInCompany) || 0;
  }

  get totalSharesInCompany() {
    return totalNumberOfShares(this.allShareholders, this.sharesForShareholders, this.shareTypes);
  }

  get totalShareCapital() {
    return this.totalSharesInCompany * this.shareNominalValue;
  }

  getSelectedNumberOfSharesForShareholder(s: ShareholderDTO) {
    return this.selectedShareType
      ? sharesForShareholderByShareType(s, this.selectedShareType, this.sharesForShareholders)
      : total(getSharesForShareholder(s, this.sharesForShareholders));
  }

  setShowRegistrationDialog() {
    this.showRegistrationDialog = true;
  }

  hideRegistrationDialog() {
    this.showRegistrationDialog = false;
  }

  sendUpdateMinutesLiveParticipationEvent(participationList: GeneralMeetingParticipation[]) {
    this.dispatch(updateMinutesLiveParticipationEvent(participationList));
  }

  getShowVotingDialogForType(voteType: GeneralMeetingVoteType, decisionId?: string) {
    return this.currentVoteType === voteType && this.decisionId === decisionId;
  }

  setShowVotingDialogForType(voteType: GeneralMeetingVoteType, decisionId?: string) {
    this.currentVoteType = voteType;
    this.decisionId = decisionId;
  }

  hideVotingDialog() {
    this.currentVoteType = undefined;
    this.decisionId = undefined;
  }

  setShowGeneralMeetingDialogs(voteType?: GeneralMeetingVoteType, decisionId?: string) {
    if (voteType !== undefined) {
      this.setShowVotingDialogForType(voteType, decisionId);
    }
    this.setShowRegistrationDialog();
  }

  hideGeneralMeetingDialogs(attendingShareholders?: GeneralMeetingParticipation[]) {
    this.hideVotingDialog();
    this.hideRegistrationDialog();
    this.shareholderNameFilter.clear();
    if (attendingShareholders !== undefined) {
      this.sendUpdateMinutesLiveParticipationEvent(attendingShareholders);
    }
  }

  getVoteForShareholder(
    shareholderId: number,
    meeting: ElectronicGeneralMeeting,
  ): GeneralMeetingIndividualVote | undefined {
    if (meeting) {
      switch (this.currentVoteType) {
        case GeneralMeetingVoteType.meetChair:
          return meeting.votes?.meetChair.find((v) => v.shareholderId === shareholderId);
        case GeneralMeetingVoteType.approveNoticeAndAgenda:
          return meeting.votes?.approveNoticeAndAgenda.find(
            (v) => v.shareholderId === shareholderId,
          );
        case "decision":
          return this.decisionId
            ? meeting.votes?.decisions[this.decisionId]?.find(
                (v) => v.shareholderId === shareholderId,
              )
            : undefined;
      }
    }
    return undefined;
  }

  *voteAsAdmin(
    shareholderId: number,
    vote: IndividualVote,
    projectId: string,
    meeting: ElectronicGeneralMeeting,
  ) {
    if (!this.isRegisteringVotesForShareholder(shareholderId)) {
      this.registeringVotesShareholderIds.push(shareholderId);
      const voteId = this.getVoteForShareholder(shareholderId, meeting);
      try {
        const participant: ElectronicParticipation = yield this.http
          .post(`/general-meeting/${projectId}/admin-vote`, {
            shareholderId,
            voteType: this.currentVoteType,
            vote,
            voteId,
            decisionId: this.decisionId,
          })
          .then((r) => r.data);
        if (meeting.votes) {
          switch (this.currentVoteType) {
            case "meetChair":
              meeting.votes.meetChair = compact([
                ...meeting.votes.meetChair.filter((v) => v.shareholderId !== shareholderId),
                participant.meetingChairVote,
              ]);
              break;
            case "approveNoticeAndAgenda":
              meeting.votes.approveNoticeAndAgenda = compact([
                ...meeting.votes.approveNoticeAndAgenda.filter(
                  (v) => v.shareholderId !== shareholderId,
                ),
                participant.approveAgendaVote,
              ]);
              break;
            case "decision":
              if (this.decisionId) {
                meeting.votes.decisions[this.decisionId] = compact([
                  ...meeting.votes.decisions[this.decisionId].filter(
                    (v) => v.shareholderId !== shareholderId,
                  ),
                  participant.decisionVotes.find((d) => d.decisionId === this.decisionId)?.vote,
                ]);
              }
              break;
          }
        }
      } catch (e) {
        this.result = failure({ key: "general-meeting.voting.errorVote" });
      }
      this.registeringVotesShareholderIds = this.registeringVotesShareholderIds.filter(
        (n) => n !== shareholderId,
      );
    }
  }

  isRegisteringVotesForShareholder(shareholderId: number) {
    return this.registeringVotesShareholderIds.includes(shareholderId);
  }

  *deleteVoteAsAdmin(shareholderId: number, projectId: string, meeting: ElectronicGeneralMeeting) {
    if (!this.isDeletingVotesForShareholder(shareholderId)) {
      this.deleteVotesShareholderIds.push(shareholderId);
      const voteId = this.getVoteForShareholder(shareholderId, meeting)?.id;
      try {
        yield this.http.delete(`/general-meeting/${projectId}/admin-vote/${voteId}`);
        if (meeting.votes) {
          switch (this.currentVoteType) {
            case "meetChair":
              meeting.votes.meetChair = meeting.votes.meetChair.filter(
                (v) => v.shareholderId !== shareholderId,
              );
              break;
            case "approveNoticeAndAgenda":
              meeting.votes.approveNoticeAndAgenda = meeting.votes.approveNoticeAndAgenda.filter(
                (v) => v.shareholderId !== shareholderId,
              );
              break;
            case "decision":
              if (this.decisionId) {
                meeting.votes.decisions[this.decisionId] = meeting.votes.decisions[
                  this.decisionId
                ].filter((v) => v.shareholderId !== shareholderId);
              }
              break;
          }
        }
      } catch (e) {
        this.result = failure({ key: "general-meeting.voting.errorVote" });
      }
      this.deleteVotesShareholderIds = this.deleteVotesShareholderIds.filter(
        (n) => n !== shareholderId,
      );
    }
  }

  isDeletingVotesForShareholder(shareholderId: number) {
    return this.deleteVotesShareholderIds.includes(shareholderId);
  }

  createAttendanceTableData(
    shareholders: ShareholderDTO[],
    data: AttendanceData,
  ): ShareholderAttendanceRow[] {
    return shareholders.map((s) => ({
      id: s.id,
      shareholderId: s.id,
      name: s.name,
      representative: this.hasPoa(s.id) ? this.getPoaName(s) : getFullName(s.contactInfo),
      attendance: data.attendingShareholders.some((a) => a.id === s.id),
      birthDateOrOrganisationNumber: s.birthDateOrOrganisationNumber,
      numberOfShares: this.getSelectedNumberOfSharesForShareholder(s),
      percentageOfShares: this.getPercentageOfSharesForShareholder(s),
      shareCapital: this.getSelectedShareCapitalForShareholder(s),
      email: s.email,
    }));
  }

  createVotingTableData(data: LiveVotingResults): ShareholderVoteRow[] {
    const shareholders = this.filterVotingShareholders(data);

    return shareholders.map((s) => ({
      id: s.id,
      shareholderId: s.id,
      name: s.name,
      representative: this.hasPoa(s.id) ? this.getPoaName(s) : getFullName(s.contactInfo),
      vote: this.getVote(s.id, data),
      birthDateOrOrganisationNumber: s.birthDateOrOrganisationNumber,
      numberOfShares: this.getSelectedNumberOfSharesForShareholder(s),
      percentageOfShares: this.getPercentageOfSharesForShareholder(s),
      shareCapital: this.getSelectedShareCapitalForShareholder(s),
      email: s.email,
    }));
  }

  getVote(shareholderId: number, data: LiveVotingResults): IndividualVote | undefined {
    if (data.shareholdersFor.some((s) => s.id === shareholderId)) {
      return "for";
    }
    if (data.shareholdersAgainst.some((s) => s.id === shareholderId)) {
      return "against";
    }
    return undefined;
  }

  toggleIncludeListOfParticipantsInMinutes() {
    this.projectContext.generalMeetingFields?.includeListOfParticipantsInMinutes.setValue(
      !this.projectContext.generalMeetingFields?.includeListOfParticipantsInMinutes.value,
    );
  }

  *startMeeting(state: StartMeetingDialogState) {
    const payload = state.form.toGeneralMeetingSettings();
    state.isSubmitting = true;
    try {
      const response: AxiosResponse<ElectronicGeneralMeeting> = yield this.http.post(
        `/general-meeting/${this.projectId}/start`,
        payload,
      );
      this.updateMeeting(response.data);
    } catch (e) {
      this.result = failure({ key: "general-error" });
    } finally {
      this.closeMeetingDialog();
    }
  }

  *endMeeting(state: EndMeetingDialogState) {
    const payload = state.form.toGeneralMeetingSettings();
    state.isSubmitting = true;
    try {
      const response: AxiosResponse<ElectronicGeneralMeeting> = yield this.http.post(
        `/general-meeting/${this.projectId}/end`,
        payload,
      );
      this.updateMeeting(response.data);
      if (this.votesModel !== undefined) {
        this.dispatch({
          type: "dbot/update-all-votes-in-general-meeting",
          votes: this.votesModel.toJSON(),
        });
      }
    } catch (e) {
      this.result = failure({ key: "general-error" });
    } finally {
      this.closeMeetingDialog();
    }
  }

  *meetingAnnouncement(state: MeetingAnnouncementDialogState) {
    const payload = state.form.toGeneralMeetingAnnouncement();
    if (state.form.isValid) {
      state.isSubmitting = true;
      try {
        yield this.http.post(`/general-meeting/${this.projectId}/announcement`, payload);
        this.dispatch([showSuccess({ key: "meetings.general-meeting.announcement.success" })]);
      } catch (e) {
        this.result = failure({ key: "general-error" });
      } finally {
        this.closeMeetingDialog();
      }
    }
  }

  reopenMeeting() {
    this.startMeeting({
      type: "start",
      form: new MeetingStartForm(this.meetingHandle()),
    });
  }

  *saveSettings(state: MeetingSettingsDialogState) {
    const payload = state.form.toGeneralMeetingSettings();
    state.isSubmitting = true;
    try {
      const res: AxiosResponse<ElectronicGeneralMeeting> = yield this.http.post(
        `/general-meeting/${this.projectId}/save-settings`,
        payload,
      );
      this.updateMeeting(res.data);
    } catch (err) {
      this.result = failure({ key: "general-error" });
    } finally {
      this.closeMeetingDialog();
    }
  }
}

export function updateVotesModelWithLiveData(
  meeting: ElectronicGeneralMeeting,
  initialState: Evaluation,
  votesModel?: GeneralMeetingVotesModel,
) {
  if (votesModel) {
    const updateRepresentedVotes = (
      model: GeneralMeetingVotingModel,
      votes?: GeneralMeetingIndividualVote[],
    ) => {
      const liveVotes = mapToVotingData(initialState, meeting, votes ?? []);
      model.setLiveVotingData(
        liveVotes.votesFor.numberOfVotes,
        liveVotes.votesAgainst.numberOfVotes,
        liveVotes.representedVotes,
        liveVotes.votesFor.sharecapital,
        liveVotes.votesAgainst.sharecapital,
        liveVotes.representedShareCapital,
      );
    };
    updateRepresentedVotes(votesModel.meetChair, meeting.votes?.meetChair);
    updateRepresentedVotes(
      votesModel.approveNoticeAndAgenda,
      meeting.votes?.approveNoticeAndAgenda,
    );
    votesModel.decisions.forEach((model, decisionId) => {
      updateRepresentedVotes(model, meeting.votes?.decisions[decisionId]);
    });
  }
}

export class NoticePreviewController {
  result: Result<NoticeForShareholderController, TMessage> = pending();
  onSetUserInfo: (userInfo: LoggedOnUserInfoDTO) => void;

  constructor(
    readonly http: AxiosInstance,
    readonly rootDispatch: EventDispatcher,
    onSetUserInfo: (userInfo: LoggedOnUserInfoDTO) => void,
  ) {
    this.onSetUserInfo = onSetUserInfo;
    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  setResult(result: Result<NoticeForShareholderController, TMessage>) {
    this.result = result;
  }

  *fetchMeetingPreview(projectId: string) {
    try {
      const response: GetNoticeForShareholderResponse = yield this.http
        .get<GetNoticeForShareholderResponse>(`/project/${projectId}/preview-meeting/SHAREHOLDER`)
        .then((r) => r.data);
      this.setResult(
        ok(
          new NoticeForShareholderController(
            response,
            new DownloadService(this.http),
            this.http,
            this.rootDispatch,
            `/api/document/view/pdf/${response.noticeDocumentId}/${response.orgNo}`,
            true,
          ),
        ),
      );
    } catch (e) {
      this.setResult(failure({ key: "noticeForShareholder.loadNoticeError" }));
    }
  }
}

export enum GeneralMeetingStatus {
  PUBLISHED = "PUBLISHED",
  STARTED = "STARTED",
  FINISHED = "FINISHED",
}

export enum StatusName {
  MEETING = "MEETING",
  REGISTRATION = "REGISTRATION",
  MEETING_CHAIR = "MEETING_CHAIR",
  APPROVE_AGENDA = "APPROVE_AGENDA",
  DECISION = "DECISION",
}

export type ElectronicGeneralMeeting = {
  id: string;
  projectId: string;
  noticeId?: string;
  minutesId?: string;
  startedAt?: string;
  finishedAt?: string;
  status: GeneralMeetingStatus;
  registrationStatus?: GeneralMeetingStatus;
  meetingChairStatus?: GeneralMeetingStatus;
  approveAgendaStatus?: GeneralMeetingStatus;
  decisionStatuses: DecisionStatusInGeneralMeeting[];
  participationList: GeneralMeetingParticipation[];
  votes?: GeneralMeetingIndividualVotes;
  autoOpenSelfRegistration: boolean;
  autoOpenVoting: boolean;
  publishDraftMinutes: boolean;
  publishMinutesPostMeeting: boolean;
};

export type DecisionStatusInGeneralMeeting = {
  decisionId: string;
  status: GeneralMeetingStatus;
};

export function iconForStatus(
  isNoticeSent: boolean,
  current?: GeneralMeetingStatus,
): IconName | undefined {
  if (!isNoticeSent) {
    return "clock";
  }
  if (current === GeneralMeetingStatus.FINISHED) {
    return "check";
  }
  if (current === GeneralMeetingStatus.STARTED) {
    return "arrows-clockwise";
  }

  return undefined;
}

export function labelForStatus(
  isNoticeSent: boolean,
  status?: GeneralMeetingStatus,
): TMessage | undefined {
  if (!isNoticeSent) {
    return undefined;
  }
  if (status === undefined || status === GeneralMeetingStatus.PUBLISHED) {
    return { key: "not-started" };
  }
  if (status === GeneralMeetingStatus.STARTED) {
    return { key: "opened" };
  }
  if (status === GeneralMeetingStatus.FINISHED) {
    return { key: "done" };
  }
}

export const getMeetingStatusTag = (status?: GeneralMeetingStatus): TagProps => {
  switch (status) {
    case GeneralMeetingStatus.STARTED:
      return { label: { key: "live" }, color: "warning" };
    case GeneralMeetingStatus.FINISHED:
      return { label: { key: "ended" }, color: "success" };
    default:
      return { label: { key: "not-started" }, color: "neutral" };
  }
};

export type AttendanceData = {
  totalNumberOfShareholders: number;
  numberOfAttendingShareholders: number;
  numberOfVotesAttending: number;
  attendingShareholders: ShareholderDTO[];
  shareholdersNotAttending: ShareholderDTO[];
  totalNumberOfVotes: number;
  attendingShareCapital: number;
  totalShareCapital: number;
};

export function isAttending(data: AttendanceData, shareholderId: number) {
  return data.attendingShareholders.some((s) => s.id === shareholderId);
}

function calculateVotes(
  meeting: ElectronicGeneralMeeting,
  sharesForShareholders: SharesForShareholder[],
  shareTypes: ShareType[],
  nominalValue: number,
) {
  const numberOfShares = (shareholder: ShareholderDTO) =>
    toNumberOfShares(shareholder, sharesForShareholders, shareTypes).reduce(
      (a, c) => a + c.numberOfShares,
      0,
    );

  return {
    isAttending: (shareholder: ShareholderDTO) =>
      meeting.participationList?.some((p) => p.shareholderId === shareholder.id && p.isPresent),
    toVotes: (shareholder: ShareholderDTO) =>
      toNumberOfVotes(shareholder, sharesForShareholders, shareTypes).reduce(sum, 0),
    numberOfShares,
    toShareCapital: (shareholder: ShareholderDTO) =>
      Big(numberOfShares(shareholder)).mul(nominalValue).toNumber(),
  };
}

export function mapToAttendanceData(
  initialState: Evaluation,
  meeting: ElectronicGeneralMeeting,
): AttendanceData {
  const { isAttending, toVotes, toShareCapital, numberOfShares } = calculateVotes(
    meeting,
    initialState.sharesForShareholders,
    initialState.shareTypes,
    initialState.shareNominalValue,
  );

  const toTotalShareCapital = (shareholder: ShareholderDTO) =>
    Big(numberOfShares(shareholder)).mul(initialState.shareNominalValue).toNumber();

  const shareholders = shareholdersWithSharesExcludingSelf(
    initialState.shareholders,
    initialState.sharesForShareholders,
  );
  const attendingShareholders = shareholders.filter(isAttending);
  return {
    totalNumberOfShareholders: shareholders.length,
    attendingShareholders,
    shareholdersNotAttending: shareholders.filter((s) => !isAttending(s)),
    numberOfAttendingShareholders: shareholders.filter(isAttending).length,
    numberOfVotesAttending: shareholders.filter(isAttending).map(toVotes).reduce(sum, 0),
    attendingShareCapital: shareholders.filter(isAttending).map(toShareCapital).reduce(sum, 0),
    totalNumberOfVotes: shareholders.map(toVotes).reduce(sum, 0),
    totalShareCapital: shareholders.map(toTotalShareCapital).reduce(sum, 0),
  };
}

export type LiveVotingResults = {
  representedVotes: number;
  representedShareholders: number;
  totalShareCapital: number;
  representedShareCapital: number;
  votesFor: VotesData;
  votesAgainst: VotesData;
  shareholders: ShareholderDTO[];
  shareholdersFor: ShareholderDTO[];
  shareholdersAgainst: ShareholderDTO[];
  shareholdersNotVoted: ShareholderDTO[];
};

export type VotesData = {
  numberOfVotes: number;
  numberOfShareholders: number;
  sharecapital: number;
};

const sum = (a: number, c: number) => a + c;

export function mapToVotingData(
  initialState: Evaluation,
  meeting: ElectronicGeneralMeeting,
  votes: GeneralMeetingIndividualVote[],
): LiveVotingResults {
  const { isAttending, toVotes, toShareCapital } = calculateVotes(
    meeting,
    initialState.sharesForShareholders,
    initialState.shareTypes,
    initialState.shareNominalValue,
  );
  const shareholders = shareholdersWithSharesExcludingSelf(
    initialState.shareholders,
    initialState.sharesForShareholders,
  );
  const attendingShareholders = shareholders.filter(isAttending);
  const votesFor = votes.filter((v) => v.vote === "for");
  const shareholdersFor = attendingShareholders.filter(
    (s) => votesFor.find((v) => v.shareholderId === s.id) !== undefined,
  );
  const votesAgainst = votes.filter((v) => v.vote === "against");
  const shareholdersAgainst = attendingShareholders.filter(
    (s) => votesAgainst.find((v) => v.shareholderId === s.id) !== undefined,
  );

  const shareholdersNotVoted = attendingShareholders.filter(
    (s) => !votes.some((v) => v.shareholderId === s.id),
  );
  const representedVotes = attendingShareholders.map(toVotes).reduce(sum, 0);
  const representedShareCapital = attendingShareholders.map(toShareCapital).reduce(sum, 0);
  return {
    representedVotes,
    representedShareCapital,
    shareholders: attendingShareholders,
    shareholdersFor,
    shareholdersAgainst,
    shareholdersNotVoted,
    representedShareholders: attendingShareholders.length,
    totalShareCapital: shareholders.map(toShareCapital).reduce(sum, 0),
    votesFor: {
      numberOfVotes: shareholdersFor.map(toVotes).reduce(sum, 0),
      numberOfShareholders: shareholdersFor.length,
      sharecapital: shareholdersFor.map(toShareCapital).reduce(sum, 0),
    },
    votesAgainst: {
      numberOfVotes: shareholdersAgainst.map(toVotes).reduce(sum, 0),
      numberOfShareholders: votes.filter((v) => v.vote === "against").length,
      sharecapital: shareholdersAgainst.map(toShareCapital).reduce(sum, 0),
    },
  };
}

function toNumberOfVotes(
  shareholder: ShareholderDTO,
  sharesForShareholders: SharesForShareholder[],
  sharetypes: ShareType[],
) {
  return toNumberOfShares(shareholder, sharesForShareholders, sharetypes).map((i) =>
    Big(i.numberOfShares).mul(i.sharetype.vote).toNumber(),
  );
}

function toShareCapital(
  shareholder: ShareholderDTO,
  sharesForShareholders: SharesForShareholder[],
  sharetypes: ShareType[],
  nominalValue: number,
) {
  return toNumberOfShares(shareholder, sharesForShareholders, sharetypes).map((i) =>
    Big(i.numberOfShares).mul(nominalValue).toNumber(),
  );
}

export function totalNumberOfVotes(
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
  sharetypes: ShareType[],
) {
  return shareholders
    .flatMap((s) => toNumberOfVotes(s, sharesForShareholders, sharetypes))
    .reduce((a, c) => a + c, 0);
}

export function totalNumberOfShares(
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
  sharetypes: ShareType[],
) {
  return shareholders
    .flatMap((s) => toNumberOfShares(s, sharesForShareholders, sharetypes))
    .reduce((a, c) => a + c.numberOfShares, 0);
}

export function toNumberOfShares(
  shareholder: ShareholderDTO,
  sharesForShareholders: SharesForShareholder[],
  sharetypes: ShareType[],
): NumberOfShares[] {
  return getSharesForShareholder(shareholder, sharesForShareholders).map<NumberOfShares>(
    ({ shareTypeId, numberOfShares }) => ({
      sharetype: sharetypes.find((st) => st.sharetypeId === shareTypeId)!,
      numberOfShares,
    }),
  );
}

export type NumberOfShares = {
  sharetype: ShareType;
  numberOfShares: number;
};

export const emptyIndividualVotes: GeneralMeetingIndividualVotes = {
  meetChair: [],
  approveNoticeAndAgenda: [],
  decisions: {},
};

type ShareholderAttendanceFilter = "all" | "attending" | "notAttending" | "poa";

type ShareholderVotingFilter =
  | "participating"
  | "shareholdersNotVoted"
  | "shareholdersFor"
  | "shareholdersAgainst";

export interface ShareholderAttendanceRow {
  id: string | number;
  shareholderId: number;
  name: string;
  representative: string | undefined;
  attendance: boolean;
  birthDateOrOrganisationNumber: string | undefined;
  numberOfShares: number;
  percentageOfShares: number;
  shareCapital: number;
  email: string;
}

export interface ShareholderVoteRow {
  id: string | number;
  shareholderId: number;
  name: string;
  representative: string | undefined;
  vote: IndividualVote | undefined;
  birthDateOrOrganisationNumber: string | undefined;
  numberOfShares: number;
  percentageOfShares: number;
  shareCapital: number;
  email: string;
}

export const findGeneralMeetingMinutes = (
  jsonData: ProjectJsonData,
): GeneralMeetingMinutes | undefined =>
  jsonData.documents.find((m) => m.documentData.type === DocumentType.general_meeting)
    ?.documentData as GeneralMeetingMinutes;

export const findVotes = (
  minutes: GeneralMeetingMinutes,
  voteType: GeneralMeetingVoteType,
  decisionId?: string,
): GeneralMeetingVoting | undefined => {
  switch (voteType) {
    case "meetChair":
      return minutes.votes?.meetChair;
    case "approveNoticeAndAgenda":
      return minutes.votes?.approveNoticeAndAgenda;
    case "decision":
      return minutes.votes?.decisions.filter((i) => i.decisionId === decisionId)[0]?.voting;
  }
};

export function isShareholderAttending(data: AttendanceData, shareholderId?: number): boolean {
  return shareholderId !== undefined && shareholderId > 0 && isAttending(data, shareholderId);
}

export function isGivePowerOfAttorneyToRoleDisabled(
  data: AttendanceData,
  shareholderId?: number,
): boolean {
  return shareholderId === undefined || isShareholderAttending(data, shareholderId);
}

const generalMeetingDecisionPanels = (
  isNoticeSent: boolean,
  { currentDecisions }: { currentDecisions: DecisionItem[] },
  meeting?: ElectronicGeneralMeeting,
): DetailsPanel[] =>
  currentDecisions
    .filter((d) => d.includeInGeneralMeeting)
    .map((d) => {
      const decisionStatus = meeting?.decisionStatuses.find(
        (s) => s.decisionId === d.decisionId,
      )?.status;
      const icon = iconForStatus(isNoticeSent, decisionStatus);
      return {
        type: "generalmeeting",
        id: d.decisionId,
        documentId: "registration",
        icon: icon ?? getDecisionIcon(d.decisionData),
        label: labelForStatus(isNoticeSent, decisionStatus),
        heading: getDecisionActionLabel(d.type, d.decisionData),
        notReady: icon === "clock" || icon === "check",
        generalMeetingStep: { type: "decision", decisionId: d.decisionId },
      };
    });

const generalMeetingFixedPanels = (
  isNoticeSent: boolean,
  meeting?: ElectronicGeneralMeeting,
): DetailsPanel[] => [
  {
    type: "generalmeeting",
    id: "registration",
    documentId: "registration",
    icon: iconForStatus(isNoticeSent, meeting?.registrationStatus) ?? "users",
    label: labelForStatus(isNoticeSent, meeting?.registrationStatus),
    heading: { key: "general-meeting.register" },
    notReady: !isNoticeSent || meeting?.registrationStatus === GeneralMeetingStatus.FINISHED,
    generalMeetingStep: { type: "registration" },
  },
  {
    type: "generalmeeting",
    id: "choiceOfMeetingChair",
    documentId: "choiceOfMeetingChair",
    icon: iconForStatus(isNoticeSent, meeting?.meetingChairStatus) ?? "gavel",
    label: labelForStatus(isNoticeSent, meeting?.meetingChairStatus),
    heading: { key: "documents.generalMeeting.choiceOfMeetingChair" },
    notReady: !isNoticeSent || meeting?.meetingChairStatus === GeneralMeetingStatus.FINISHED,
    generalMeetingStep: { type: "choiceOfMeetingChair" },
  },
  {
    type: "generalmeeting",
    id: "approveAgenda",
    documentId: "approveAgenda",
    icon: iconForStatus(isNoticeSent, meeting?.approveAgendaStatus) ?? "gavel",
    label: labelForStatus(isNoticeSent, meeting?.approveAgendaStatus),
    heading: { key: "documents.generalMeeting.approveAgenda" },
    notReady: !isNoticeSent || meeting?.approveAgendaStatus === GeneralMeetingStatus.FINISHED,
    generalMeetingStep: { type: "approveAgenda" },
  },
];

export const generalMeetingPanels = (
  isNoticeSent: boolean,
  context: { currentDecisions: DecisionItem[] },
  meeting?: ElectronicGeneralMeeting,
) => [
  ...generalMeetingFixedPanels(isNoticeSent, meeting),
  ...generalMeetingDecisionPanels(isNoticeSent, context, meeting),
];

export class MeetingSettingsForm {
  meeting?: ElectronicGeneralMeeting;
  isPublishMinutesPostMeetingSelected: boolean;
  meetingSettings: OptionFieldModel<MeetingSettingsOption>;

  constructor(meeting?: ElectronicGeneralMeeting) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.meeting = meeting;
    this.meetingSettings = new OptionFieldModel<MeetingSettingsOption>({
      options: Object.values(MeetingSettingsOption),
      idFn: (v) => String(v),
      labelFn: (v) => meetingSettingsStartLabel[v],
      value: compact([
        meeting?.autoOpenSelfRegistration && MeetingSettingsOption.AUTO_OPEN_SELF_REGISTRATION,
        meeting?.autoOpenVoting && MeetingSettingsOption.AUTO_OPEN_VOTING,
      ]),
    });
    this.isPublishMinutesPostMeetingSelected = meeting?.publishMinutesPostMeeting ?? false;
  }

  get isStartOptionsDisabled(): boolean {
    return this.meeting?.status === GeneralMeetingStatus.STARTED;
  }

  get isReadonly(): boolean {
    return this.isStartOptionsDisabled;
  }

  toGeneralMeetingSettings(): GeneralMeetingSettings {
    return {
      autoOpenSelfRegistration: this.meetingSettings.isSelected(
        MeetingSettingsOption.AUTO_OPEN_SELF_REGISTRATION,
      ),
      autoOpenVoting: this.meetingSettings.isSelected(MeetingSettingsOption.AUTO_OPEN_VOTING),
    };
  }
}

export class MeetingStartForm {
  meeting?: ElectronicGeneralMeeting;
  meetingStart: OptionFieldModel<MeetingSettingsOption>;

  constructor(meeting?: ElectronicGeneralMeeting) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.meeting = meeting;
    this.meetingStart = new OptionFieldModel<MeetingSettingsOption>({
      options: Object.values(MeetingSettingsOption),
      idFn: (v) => String(v),
      labelFn: (v) => meetingSettingsStartLabel[v],
      value: compact([
        meeting?.autoOpenSelfRegistration && MeetingSettingsOption.AUTO_OPEN_SELF_REGISTRATION,
        meeting?.autoOpenVoting && MeetingSettingsOption.AUTO_OPEN_VOTING,
      ]),
    });
  }

  toGeneralMeetingSettings(): GeneralMeetingSettings {
    return {
      autoOpenSelfRegistration: this.meetingStart.isSelected(
        MeetingSettingsOption.AUTO_OPEN_SELF_REGISTRATION,
      ),
      autoOpenVoting: this.meetingStart.isSelected(MeetingSettingsOption.AUTO_OPEN_VOTING),
    };
  }
}

export class MeetingEndForm {
  meeting?: ElectronicGeneralMeeting;
  isPublishMinutesPostMeetingSelected: boolean;

  constructor(meeting?: ElectronicGeneralMeeting) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.meeting = meeting;
    this.isPublishMinutesPostMeetingSelected = meeting?.publishMinutesPostMeeting ?? false;
  }

  get isAllMattersResolved() {
    return (
      this.meeting?.registrationStatus === GeneralMeetingStatus.FINISHED &&
      this.meeting?.meetingChairStatus === GeneralMeetingStatus.FINISHED &&
      this.meeting?.approveAgendaStatus === GeneralMeetingStatus.FINISHED &&
      this.meeting?.decisionStatuses.every((d) => d.status === GeneralMeetingStatus.FINISHED)
    );
  }

  toGeneralMeetingSettings(): GeneralMeetingSettings {
    return {
      autoOpenSelfRegistration: this.meeting?.autoOpenSelfRegistration ?? false,
      autoOpenVoting: this.meeting?.autoOpenVoting ?? false,
    };
  }
}

export class MeetingAnnouncementForm {
  message: StringFieldModel;
  copyByEmail: boolean;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.message = new StringFieldModel({ required: true });
    this.copyByEmail = false;
  }

  setCopyByEmail(selected: boolean) {
    this.copyByEmail = selected;
  }

  get isValid(): boolean {
    return this.message.isValid;
  }

  validate(): boolean {
    this.message.validate();
    return this.isValid;
  }

  toGeneralMeetingAnnouncement(): GeneralMeetingAnnouncement {
    this.message.commit();
    this.validate();
    return {
      message: this.message.validatedValue,
      copyByEmail: this.copyByEmail,
    };
  }
}

export class CopyNoticeUrlForShareholderForm {
  shareholder: ShareholderAttendanceRow;
  url: string;
  companyName: string;
  dateGeneralMeeting: string | undefined;
  close: () => void;
  isConfirmed: BooleanFieldModel;
  result?: Result<TMessage, TMessage>;

  constructor(
    closeDialog: () => void,
    shareholder: ShareholderAttendanceRow,
    url: string,
    companyName: string,
    dateGeneralMeeting: string | undefined,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.close = closeDialog;
    this.shareholder = shareholder;
    this.url = url;
    this.companyName = companyName;
    this.dateGeneralMeeting = dateGeneralMeeting;
    this.isConfirmed = new BooleanFieldModel({ value: false });
  }

  copyToClipboard(text: string) {
    this.result = pending();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.result = ok({ key: "general-meeting.copy-participation-link.submit.success" });
      })
      .catch((err) => {
        logError(err, "Failed to copy notice to shareholder link to clipboard");
        this.result = failure({ key: "general-error" });
      });
  }

  get canCopy() {
    return this.isConfirmed.value;
  }
}

export class MeetingResendNoticeForm {
  dispatch: DbotEventDispatcher;
  shareholder: ShareholderAttendanceRow;
  email: StringFieldModel;
  documentId: string;
  result?: Result<TMessage, TMessage>;
  close: () => void;

  constructor(
    closeDialog: () => void,
    dispatch: DbotEventDispatcher,
    shareholder: ShareholderAttendanceRow,
    documentId: string,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.close = closeDialog;
    this.dispatch = dispatch;
    this.shareholder = shareholder;
    this.email = createEmailField({ value: shareholder.email, required: true });
    this.documentId = documentId;
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/resend-notice-to-updated-recipient-result":
        if (isFailure(event.result)) {
          this.result = event.result;
        } else {
          this.result = ok({ key: "dbot.resendDocumentForUpdatingSuccess" });
        }
        break;
    }
  }

  submit() {
    this.result = pending();
    this.dispatch({
      type: "dbot/resend-notice-to-updated-recipient",
      documentId: this.documentId,
      data: this.toResendNoticeData(),
    });
  }

  toResendNoticeData(): ResendDocumentForViewing {
    return {
      recipientName: this.shareholder.name,
      recipientEmail: this.email.validatedValue!,
      shareholderId: this.shareholder.shareholderId,
      recipientDocumentId: String(this.shareholder.id),
    };
  }

  get isValid(): boolean {
    return this.email.isValid;
  }

  validate() {
    this.email.validate();
    return this.isValid;
  }
}

type RegisterPowerOfAttorneyFormParams = {
  registerNotAttending: (shareholderId: number, meeting: ElectronicGeneralMeeting) => void;
  closeDialog: () => void;
  dispatch: DbotEventDispatcher;
  http: AxiosInstance;
  projectContext: ProjectContext;
  meeting: ElectronicGeneralMeeting;
  powerOfAttorney: Map<number, PowerOfAttorneyForGeneralMeeting>;
  shareholderId?: number;
};

export class RegisterPowerOfAttorneyForm {
  params: RegisterPowerOfAttorneyFormParams;
  http: AxiosInstance;

  selectedShareholderId?: number;
  poaRecipientRoleModel: RadioFieldModel<PoARecipientRole | undefined>;
  poaOtherRecipientModel: PoAOtherRecipientModel;
  allShareholderOptions: PowerOfAttorneyShareholderOption[];
  shareholderRecipientModel?: PowerOfAttorneyShareholderOption;
  givePoAResult?: Result<TMessage, TMessage>;

  constructor(params: RegisterPowerOfAttorneyFormParams) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
    this.params = params;
    this.http = params.http;

    this.selectedShareholderId = params.shareholderId;
    this.poaRecipientRoleModel = new RadioFieldModel<PoARecipientRole | undefined>({
      value: undefined,
      options:
        this.ceoName === ""
          ? [
              undefined,
              ...Object.values(PoARecipientRole).filter((role) => role !== PoARecipientRole.ceo),
            ]
          : [undefined, ...Object.values(PoARecipientRole)],
      idFn: identity,
      labelFn: this.getLabelForRole,
      required: true,
    });
    this.allShareholderOptions = this.allShareholders.map((d) => {
      return {
        id: d.id,
        name: d.name,
        representative: d.isCompany ? getFullName(d.contactInfo) : undefined,
        email: d.email,
      };
    });
    this.poaOtherRecipientModel = new PoAOtherRecipientModel(this.shareholderOptions);
    this.setSelectedShareholder(this.selectedShareholder);
  }

  get context() {
    return this.params.projectContext;
  }

  get powerOfAttorney() {
    return this.params.powerOfAttorney;
  }

  close() {
    this.params.closeDialog();
  }

  get attendance(): AttendanceData {
    return mapToAttendanceData(this.context.initialState, this.params.meeting);
  }

  get allShareholders() {
    return shareholdersWithSharesExcludingSelf(
      this.context.initialState.shareholders,
      this.context.initialState.sharesForShareholders,
    );
  }

  get ceoName() {
    return getFullName(this.context.initialState.roles.find((d) => d.role === RoleType.CEO));
  }

  get chairpersonName() {
    return getFullName(
      this.context.initialState.roles.find((d) => d.role === RoleType.CHAIRPERSON),
    );
  }

  get companyName(): string {
    return this.context.initialState.company.name;
  }

  getLabelForRole(value: PoARecipientRole | undefined): TMessage {
    switch (value) {
      case PoARecipientRole.ceo:
        return { key: "ceo-with-name", params: { name: this.ceoName } };
      case PoARecipientRole.chairperson:
        return {
          key: "chairperson-with-name",
          params: { name: this.chairpersonName },
        };
      case PoARecipientRole.shareholder:
        return {
          key: "noticeForShareholder.poaShareholder",
        };
      case PoARecipientRole.other:
        return { key: "noticeForShareholder.otherNamedRecipient" };
      default:
        return { key: "noticeForShareholder.representingSelf" };
    }
  }

  get poaRecipientName(): string {
    switch (this.poaRecipientRoleModel.value) {
      case PoARecipientRole.chairperson:
        return this.chairpersonName;
      case PoARecipientRole.ceo:
        return this.ceoName;
      case PoARecipientRole.shareholder:
        return (
          this.shareholderRecipientModel?.representative ??
          this.shareholderRecipientModel?.name ??
          ""
        );
      case PoARecipientRole.other:
        return this.poaOtherRecipientModel.name.validatedValue;
      default:
        return "";
    }
  }

  get givenPowerOfAttorneyTo(): string | undefined {
    return this.selectedShareholderId
      ? this.powerOfAttorney.get(this.selectedShareholderId)?.poaRecipientName
      : undefined;
  }

  get givenPowerOfAttorney(): boolean {
    return this.selectedShareholderId
      ? this.powerOfAttorney.has(this.selectedShareholderId)
      : false;
  }

  get isShareholderCompany() {
    return this.selectedShareholder?.isCompany ?? false;
  }

  get shareholderName() {
    return this.selectedShareholder?.name ?? "";
  }

  get contactPersonName() {
    return getFullName(this.selectedShareholder?.contactInfo) ?? "";
  }

  get shareholderOptions() {
    return this.allShareholderOptions.filter((d) => d.id !== this.selectedShareholderId);
  }

  get selectedShareholder() {
    return this.allShareholders.find((d) => d.id === this.selectedShareholderId);
  }

  setSelectedShareholder(value?: ShareholderDTO) {
    const poa = value ? this.powerOfAttorney.get(value.id) : undefined;
    this.poaRecipientRoleModel.setValue(poa?.poaRecipientRole);
    this.shareholderRecipientModel = this.shareholderOptions.find(
      (d) => d.id === poa?.poaRecipientShareholderId,
    );

    this.poaOtherRecipientModel.name.setInputValue(
      poa?.poaRecipientRole === PoARecipientRole.other ? (poa?.poaRecipientName ?? "") : "",
    );
    this.poaOtherRecipientModel.email.setInputValue(
      poa?.poaRecipientRole === PoARecipientRole.other ? (poa?.poaRecipientEmail ?? "") : "",
    );

    this.selectedShareholderId = value?.id;
  }

  get isAdminPreview() {
    return true;
  }

  get isSendPoADisabled(): boolean {
    const powerOfAttorney = this.selectedShareholderId
      ? this.powerOfAttorney.get(this.selectedShareholderId)
      : undefined;
    const isOther =
      this.poaRecipientRoleModel.value === PoARecipientRole.other &&
      this.poaOtherRecipientModel.isSendOtherDisabled;

    const isShareholder =
      this.poaRecipientRoleModel.value === PoARecipientRole.shareholder &&
      this.shareholderRecipientModel === undefined;

    const isNotChanged =
      powerOfAttorney?.poaRecipientName === this.poaRecipientName &&
      powerOfAttorney?.poaRecipientRole === this.poaRecipientRoleModel.value;

    return this.selectedShareholderId === undefined || isOther || isShareholder || isNotChanged;
  }

  validate() {
    this.poaRecipientRoleModel.validate();
    this.poaOtherRecipientModel.validate();
  }

  get isValid() {
    const isShareholderRecipientValid =
      this.poaRecipientRoleModel.value === PoARecipientRole.shareholder &&
      this.shareholderRecipientModel !== undefined;
    const isOtherRecipientValid =
      this.poaRecipientRoleModel.value === PoARecipientRole.other &&
      this.poaOtherRecipientModel.isValid;
    return (
      this.selectedShareholderId !== undefined &&
      (this.poaRecipientRoleModel.isValid || isShareholderRecipientValid || isOtherRecipientValid)
    );
  }

  submit() {
    switch (this.poaRecipientRoleModel.value) {
      case undefined:
        this.revokePoA();
        break;
      default:
        this.sendPoA();
        break;
    }
  }

  *sendPoA() {
    this.givePoAResult = pending();
    this.validate();
    if (!this.isValid) {
      this.givePoAResult = failure({ key: "general-error" });
      return;
    }

    const poaRecipient = toPoARecipient(
      this.chairpersonName,
      this.ceoName,
      this.poaRecipientRoleModel.value,
      this.poaOtherRecipientModel,
      this.shareholderRecipientModel,
    );
    try {
      yield this.http.post(
        `/general-meeting/${this.context.projectId}/admin-poa/${this.selectedShareholderId}`,
        {
          poaRecipientRole: poaRecipient?.role,
          poaRecipientShareholderId: poaRecipient?.shareholderId,
          poaRecipientName: poaRecipient?.name,
          poaRecipientEmail: poaRecipient?.email,
        },
      );
      this.givePoAResult = ok({
        key: "noticeForShareholder.poaGiven",
        params: { name: poaRecipient?.name },
      });
    } catch (error) {
      this.givePoAResult = failure({ key: "noticeForShareholder.decisions.failedToGivePoA" });
    }
  }

  *revokePoA() {
    this.givePoAResult = pending();
    const shareholderId = this.selectedShareholderId;
    if (shareholderId === undefined) {
      this.givePoAResult = failure({ key: "general-error" });
      return;
    }

    try {
      yield this.http.delete(
        `/general-meeting/${this.context.projectId}/admin-poa/${shareholderId}`,
      );
      this.givePoAResult = ok({ key: "noticeForShareholder.poaRevoked" });
    } catch (error) {
      this.givePoAResult = failure({ key: "noticeForShareholder.decisions.failedToRevokePoA" });
    }
  }

  removeAttendance() {
    if (this.selectedShareholderId) {
      this.params.registerNotAttending(this.selectedShareholderId, this.params.meeting);
    }
  }
}

export type ElectronicGeneralMeetingAlertMessage = {
  messageType: AlertMessageType;
  message: TMessage;
  openAgendaItem?: AgendaItemType;
  linkText?: TMessage;
};

function meetingStatusUpdateMessage(
  status: GeneralMeetingStatus,
): ElectronicGeneralMeetingAlertMessage {
  if (status === GeneralMeetingStatus.STARTED) {
    return {
      messageType: "info",
      message: { key: "general-meeting.started-info" },
      linkText: { key: "general-meeting.started-info.link" },
    };
  } else {
    return {
      messageType: "info",
      message: { key: "general-meeting.ended-info" },
    };
  }
}

function registrationStatusUpdateMessage(
  status: GeneralMeetingStatus,
  isAttending: boolean,
): ElectronicGeneralMeetingAlertMessage {
  if (status === GeneralMeetingStatus.STARTED) {
    return {
      messageType: "info",
      message: { key: "general-meeting.self-registration-opened" },
      openAgendaItem: attendanceItem,
      linkText: { key: "general-meeting.register-now" },
    };
  } else {
    return {
      messageType: "info",
      message: {
        key: isAttending
          ? "general-meeting.self-registration-closed-attending"
          : "general-meeting.self-registration-closed-not-attending",
      },
    };
  }
}

function agendaItemVotingUpdateMessage(
  status: GeneralMeetingStatus,
  openAgendaItem: AgendaItemType,
  agendaLabel: TMessage,
  voted: () => GeneralMeetingIndividualVote | undefined,
  isInformationMatter?: boolean,
): ElectronicGeneralMeetingAlertMessage {
  if (status === GeneralMeetingStatus.STARTED) {
    return {
      messageType: "info",
      message: {
        key: isInformationMatter
          ? "general-meeting.information-matter.matter-started"
          : "voting-started",
        params: { name: agendaLabel },
      },
      openAgendaItem,
      linkText: { key: isInformationMatter ? "show-more" : "general-meeting.vote-now" },
    };
  } else {
    const shareholderVoted = voted();
    return {
      messageType: "info",
      message: isInformationMatter
        ? { key: "general-meeting.information-matter.matter-ended", params: { name: agendaLabel } }
        : {
            key: "voting-ended",
            params: {
              name: agendaLabel,
              voteOutcome: shareholderVoted
                ? {
                    key: "voting.you-voted",
                    params: { vote: voteLabelUppercase(shareholderVoted.vote) },
                  }
                : { key: "voting.you-did-not-vote" },
            },
          },
    };
  }
}

function agendaItemVoteByAdminMessage(
  agendaLabel: TMessage,
  voted: () => GeneralMeetingIndividualVote | undefined,
): ElectronicGeneralMeetingAlertMessage {
  const shareholderVoted = voted();
  if (shareholderVoted) {
    return {
      messageType: "success",
      message: {
        key: "vote-registered-by-admin",
        params: {
          name: agendaLabel,
          vote: voteLabelUppercase(shareholderVoted.vote),
        },
      },
    };
  } else {
    return {
      messageType: "info",
      message: { key: "vote-reset-by-admin", params: { name: agendaLabel } },
    };
  }
}

const electronicParticipation = (
  response?: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
): ElectronicParticipation | undefined =>
  response?.shareholderParticipation?.find((p) => p.shareholderId === response.shareholderId);

export const mapToElectronicGeneralMeetingAlertMessage = ({
  statusName,
  status,
  isAttending,
  response,
  decisionId,
}: {
  statusName: StatusName;
  status: GeneralMeetingStatus;
  isAttending: boolean;
  response?: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  decisionId?: string;
}): ElectronicGeneralMeetingAlertMessage => {
  switch (statusName) {
    case StatusName.MEETING:
      return meetingStatusUpdateMessage(status);
    case StatusName.REGISTRATION:
      return registrationStatusUpdateMessage(status, isAttending);
    case StatusName.MEETING_CHAIR:
      return agendaItemVotingUpdateMessage(
        status,
        meetingChairItem,
        { key: "documents.generalMeeting.choiceOfMeetingChair" },
        () => electronicParticipation(response)?.meetingChairVote,
      );
    case StatusName.APPROVE_AGENDA:
      return agendaItemVotingUpdateMessage(
        status,
        approveAgendaItem,
        { key: "documents.generalMeeting.approveAgenda" },
        () => electronicParticipation(response)?.approveAgendaVote,
      );
    case StatusName.DECISION: {
      const matter = response!.mattersForVoting.find((m) => m.decision.decisionId === decisionId);
      const agendaLabel = matter
        ? getDecisionActionLabel(matter.decision.decisionData.type, matter.decision.decisionData)
        : { translated: "" };

      return agendaItemVotingUpdateMessage(
        status,
        matterForVoting(matter!),
        agendaLabel,
        () =>
          electronicParticipation(response)?.decisionVotes.find((v) => v.decisionId === decisionId)
            ?.vote,
        isInformationMatterInDocument(matter?.decision.decisionData, DocumentType.general_meeting),
      );
    }
  }
};

export const mapToAttendanceAlertMessage = ({
  shareholderId,
  projectId,
  participation,
  response,
  updateSelectedAttendances,
}: {
  shareholderId: number;
  projectId: string;
  participation?: ElectronicParticipation;
  response?: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  updateSelectedAttendances: () => void;
}): ElectronicGeneralMeetingAlertMessage | undefined => {
  const isUpdated = updateParticipation(shareholderId, projectId, participation, response);
  if (!isUpdated) {
    return undefined;
  }
  updateSelectedAttendances();
  if (participation?.isPresent) {
    return {
      messageType: "success",
      message: { key: "general-meeting.attendance.registeredByHostAttending" },
      openAgendaItem: attendanceItem,
      linkText: { key: "general-meeting.continue-to-registration" },
    };
  } else {
    return {
      messageType: "info",
      message: { key: "general-meeting.attendance.registeredByHostNotAttending" },
      openAgendaItem: attendanceItem,
      linkText: { key: "general-meeting.continue-to-registration" },
    };
  }
};

function updateParticipation(
  shareholderId: number,
  projectId: string,
  participation?: ElectronicParticipation,
  response?: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
) {
  if (response === undefined || !response.electronicGeneralMeeting) {
    logError("Participation update received without general meeting data");
    return false;
  }
  if (response.projectId !== projectId) {
    logError("Participation update received wrong project id");
    return false;
  }
  if (!response.shareholderParticipation) {
    response.shareholderParticipation = [];
  }
  const index = response.shareholderParticipation?.findIndex(
    (p) => p.shareholderId === shareholderId,
  );
  if (index !== undefined && index !== -1 && participation) {
    response.shareholderParticipation![index] = participation;
  }
  return true;
}

export const mapToVotingAlertMessage = ({
  shareholderId,
  projectId,
  participation,
  voteType,
  decisionId,
  response,
}: {
  shareholderId: number;
  projectId: string;
  participation: ElectronicParticipation;
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
  response?: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
}): ElectronicGeneralMeetingAlertMessage | undefined => {
  const isUpdated = updateParticipation(shareholderId, projectId, participation, response);
  if (!isUpdated) {
    return undefined;
  }

  switch (voteType) {
    case GeneralMeetingVoteType.meetChair:
      return agendaItemVoteByAdminMessage(
        { key: "documents.generalMeeting.choiceOfMeetingChair" },
        () => electronicParticipation(response)?.meetingChairVote,
      );
    case GeneralMeetingVoteType.approveNoticeAndAgenda:
      return agendaItemVoteByAdminMessage(
        { key: "documents.generalMeeting.approveAgenda" },
        () => electronicParticipation(response)?.approveAgendaVote,
      );
    case GeneralMeetingVoteType.decision: {
      const matter = response!.mattersForVoting.find((m) => m.decision.decisionId === decisionId);
      const agendaLabel = matter
        ? getDecisionActionLabel(matter.decision.decisionData.type, matter.decision.decisionData)
        : { translated: "" };
      return agendaItemVoteByAdminMessage(
        agendaLabel,
        () =>
          electronicParticipation(response)?.decisionVotes.find((v) => v.decisionId === decisionId)
            ?.vote,
      );
    }
  }
};

export const mapToReceivedPoAAlertMessage = ({
  projectId,
  receivedPowerOfAttorneys,
  name,
  response,
  updateSelectedAttendances,
}: {
  projectId: string;
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
  name: string;
  response?: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  updateSelectedAttendances: () => void;
}): ElectronicGeneralMeetingAlertMessage | undefined => {
  if (response === undefined || response.projectId !== projectId) {
    logError("mapToReceivedPoAAlertMessage: wrong project id", { response, projectId });
    return;
  }
  response.receivedPowerOfAttorneys = receivedPowerOfAttorneys;
  updateSelectedAttendances();

  return {
    messageType: "info",
    message: {
      key: "general-meeting.received-power-of-attorney-update.poa-received",
      params: { name },
    },
  };
};

export const mapToRevokedPoAAlertMessage = ({
  projectId,
  revokedNoticeId,
  revokerName,
  response,
}: {
  projectId: string;
  revokedNoticeId: string;
  revokerName: string;
  response?: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
}): ElectronicGeneralMeetingAlertMessage | undefined => {
  if (response === undefined || response.projectId !== projectId) {
    logError("mapToRevokedPoAAlertMessage: wrong project id", { response, projectId });
    return;
  }
  response.receivedPowerOfAttorneys = response.receivedPowerOfAttorneys.filter(
    (poa) => poa.noticeForShareholderId !== revokedNoticeId,
  );
  return {
    messageType: "info",
    message: {
      key: "general-meeting.received-power-of-attorney-update.poa-revoked",
      params: { name: revokerName },
    },
  };
};

export const headerActions = (
  ctrl: AdminElectronicGeneralMeetingController,
): {
  openInMeetings: () => void;
  openVideoMeeting?: () => void;
  toggleScreenShareMode: () => void;
} => ({
  openInMeetings: () => navigateGeneralMeetingPreview(ctrl.orgNo, ctrl.projectId),
  openVideoMeeting: when(
    ctrl.meetingLink,
    (meetingLink) => () =>
      openLinkInNewWindow(meetingLink.startsWith("http") ? meetingLink : `https://${meetingLink}`),
  ),
  toggleScreenShareMode: () => ctrl.dispatch({ type: "dbot/toggle-screen-share-mode" }),
});

export type ShareholderIntervalPricing = {
  from: number;
  to?: number;
  label: TMessage;
  price: TMessage | number;
};

const liveGeneralMeetingPricingIntervals: ShareholderIntervalPricing[] = [
  {
    from: 0,
    to: 9,
    label: { key: "shareholderInterval.1" },
    price: 2000,
  },
  {
    from: 10,
    to: 29,
    label: { key: "shareholderInterval.2" },
    price: 10000,
  },
  {
    from: 30,
    to: 59,
    label: { key: "shareholderInterval.3" },
    price: 15000,
  },
  {
    from: 60,
    to: 99,
    label: { key: "shareholderInterval.4" },
    price: 25000,
  },
  {
    from: 100,
    label: { key: "shareholderInterval.5" },
    price: { key: "shareholderInterval.contact-us" },
  },
];

export const getShareIntervals = (): ShareholderIntervalPricing[] => {
  return liveGeneralMeetingPricingIntervals;
};

export const findShareholderPricingInterval = (
  shareholderCount: number,
  availableShareIntervals: ShareholderIntervalPricing[],
) =>
  availableShareIntervals.find(
    (s) => s.from <= shareholderCount && (s.to ?? Number.MAX_SAFE_INTEGER) >= shareholderCount,
  );

export const findLiveGeneralMeetingPricingInterval = (shareholderCount: number) => {
  return liveGeneralMeetingPricingIntervals.find(
    (s) => s.from <= shareholderCount && (s.to ?? Number.MAX_SAFE_INTEGER) >= shareholderCount,
  );
};
